/**
 * F43-FC §6.6 — Affiliations tab panel on the admin platform-user detail page.
 *
 * Replaces the generic `TabRows` on the Affiliations tab. Per affiliation row it
 * renders, gated on `affiliations:moderate`:
 *   - "Suspend" — suspend the affiliation (cascade cancels future bookings,
 *     forces both visibility flags off). Hidden on terminal rows + on a row
 *     already `suspended` (matches `assertTransition`).
 *   - "End" — terminal end (cascade cancels future bookings). Hidden on
 *     terminal rows.
 *
 * REUSE: both actions route through the SAME `VenueTrainerAffiliationService`
 * cascade via the admin-actor bypass (`adminUsersApi.{suspendAffiliation,
 * endAffiliation}` → `POST /admin/affiliations/:id/{suspend,end}`), and through
 * the SAME shared `AffiliationAdminDialog` — one backing service + one shared
 * component per the founder reuse mandate.
 *
 * On success every handler refetches the USER's Affiliations tab (`onReload`),
 * not a venue/trainer page. A server error keeps the dialog open + surfaces the
 * message (the dialog's `onConfirm`-re-throw contract, §6.2).
 */
import { useState } from 'react';
import { Badge, Button, Card, EmptyState, Skeleton, useToast } from '@bravemove/ui-web';
import { useI18n } from '@bravemove/i18n/react';
import { adminUsersApi, type AdminUserTabRow } from '../../api/adminUsers';
import { translateApiError } from '../../auth/errors.js';
import { AffiliationAdminDialog } from './AffiliationAdminDialog';

/** Terminal statuses — neither Suspend nor End applies (`assertTransition`). */
const TERMINAL = new Set(['ended', 'declined', 'withdrawn']);

export function AffiliationsAdminPanel({
  rows,
  canModerate,
  onReload,
}: {
  readonly rows: readonly AdminUserTabRow[] | undefined;
  readonly canModerate: boolean;
  readonly onReload: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [active, setActive] = useState<{ row: AdminUserTabRow; mode: 'suspend' | 'end' } | null>(
    null,
  );

  if (rows === undefined) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (!Array.isArray(rows) || rows.length === 0) {
    return <EmptyState title={t('admin:f43.empty.generic')} />;
  }

  // Runs the mutation; throws a translated message on failure so the dialog
  // stays open (the AffiliationAdminDialog `onConfirm` re-throw contract).
  const run = async (reason: string) => {
    if (!active) return;
    try {
      if (active.mode === 'suspend') {
        await adminUsersApi.suspendAffiliation(active.row.id, reason);
      } else {
        await adminUsersApi.endAffiliation(active.row.id, reason);
      }
    } catch (err) {
      // Re-throw a translated message (the dialog stays open + renders it),
      // chaining the original error as `cause` (preserve-caught-error).
      throw new Error(translateApiError(err, t), { cause: err });
    }
    onReload();
    toast.push({
      tone: 'success',
      title:
        active.mode === 'suspend'
          ? t('affiliation:admin.suspended_toast', { defaultValue: 'Affiliation suspended.' })
          : t('affiliation:admin.ended_toast', { defaultValue: 'Affiliation ended.' }),
    });
  };

  return (
    <>
      <Card>
        <ul className="divide-y divide-outline" data-testid="f43-affiliations-admin">
          {rows.map((r) => {
            const status = r.status ?? '';
            const isTerminal = TERMINAL.has(status);
            return (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                data-testid={`f43-affiliation-${r.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] text-ink truncate">{r.title}</p>
                  {r.subtitle ? (
                    <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {status ? (
                    <Badge tone="neutral" size="sm">
                      {status}
                    </Badge>
                  ) : null}
                  {canModerate && !isTerminal && status !== 'suspended' ? (
                    <Button
                      variant="secondary"
                      size="xs"
                      data-testid={`f43-affiliation-suspend-${r.id}`}
                      onClick={() => setActive({ row: r, mode: 'suspend' })}
                    >
                      {t('affiliation:admin.suspend_cta', { defaultValue: 'Suspend' })}
                    </Button>
                  ) : null}
                  {canModerate && !isTerminal ? (
                    <Button
                      variant="destructive"
                      size="xs"
                      data-testid={`f43-affiliation-end-${r.id}`}
                      onClick={() => setActive({ row: r, mode: 'end' })}
                    >
                      {t('affiliation:admin.end_cta_short', { defaultValue: 'End' })}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <AffiliationAdminDialog
        open={active !== null}
        mode={active?.mode ?? 'suspend'}
        venueName={active?.row.title ?? ''}
        onClose={() => setActive(null)}
        onConfirm={async (reason) => {
          // `run` throws a translated message on failure → the dialog stays open
          // and renders it. On success it resolves and the dialog closes itself.
          await run(reason);
          setActive(null);
        }}
      />
    </>
  );
}
